import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { updateAppointmentStatusThunk } from "../../../redux/Thunk/Appointment/AppointmentThunks";

const PRESET_REASONS = [
  "Patient Requested Cancellation",
  "Patient No-Show / Did Not Arrive",
  "Doctor Unavailable / Emergency",
  "Incorrect Booking / Duplicate",
  "Fee Issue / Payment Declined",
];

const CancelAppointmentModal = ({ appointment, onClose, selectedDate }) => {
  const dispatch = useDispatch();
  const [cancelStatus, setCancelStatus] = useState("CANCELLED");
  const [selectedPreset, setSelectedPreset] = useState(PRESET_REASONS[0]);
  const [customReason, setCustomReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!appointment) return null;

  const handleConfirmCancel = async () => {
    const finalReason = customReason.trim()
      ? `${selectedPreset} - ${customReason.trim()}`
      : selectedPreset;

    setIsSubmitting(true);
    try {
      await dispatch(
        updateAppointmentStatusThunk({
          AID: appointment.AID,
          status: cancelStatus,
          cancelledReason: finalReason,
          appointmentDate: selectedDate,
        })
      ).unwrap();
      onClose();
    } catch (err) {
      console.error("Failed to cancel appointment", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-6 py-4 bg-red-50/70 border-b border-red-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-red-100 text-red-600 flex items-center justify-center font-bold">
              ✕
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Cancel Appointment</h3>
              <p className="text-xs font-semibold text-red-600">
                AID: {appointment.AID}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex items-center justify-center font-bold text-sm transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-5">
          {/* Patient Details Brief */}
          <div className="p-3 bg-gray-50 rounded-xl border border-gray-100 flex items-center justify-between text-xs">
            <div>
              <span className="text-gray-500 font-medium">Patient: </span>
              <strong className="text-gray-900 font-bold">
                {appointment.patientName}
              </strong>
            </div>
            <div>
              <span className="text-gray-500 font-medium">Time: </span>
              <span className="font-semibold text-gray-800">
                {appointment.slotStartTime} - {appointment.slotEndTime}
              </span>
            </div>
          </div>

          {/* Cancellation Type Toggle */}
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
              Cancellation Type
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => {
                  setCancelStatus("CANCELLED");
                  if (selectedPreset === "Patient No-Show / Did Not Arrive") {
                    setSelectedPreset(PRESET_REASONS[0]);
                  }
                }}
                className={`py-2 px-3 rounded-xl border text-xs font-bold transition-all ${
                  cancelStatus === "CANCELLED"
                    ? "bg-red-600 text-white border-red-600 shadow-xs"
                    : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                }`}
              >
                Standard Cancellation
              </button>
              <button
                type="button"
                onClick={() => {
                  setCancelStatus("NO_SHOW");
                  setSelectedPreset("Patient No-Show / Did Not Arrive");
                }}
                className={`py-2 px-3 rounded-xl border text-xs font-bold transition-all ${
                  cancelStatus === "NO_SHOW"
                    ? "bg-amber-600 text-white border-amber-600 shadow-xs"
                    : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                }`}
              >
                Patient No-Show
              </button>
            </div>
          </div>

          {/* Quick Preset Reasons */}
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
              Reason Preset
            </label>
            <div className="space-y-1.5">
              {PRESET_REASONS.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  onClick={() => setSelectedPreset(reason)}
                  className={`w-full text-left px-3 py-2 rounded-xl text-xs font-medium transition-all border flex items-center justify-between ${
                    selectedPreset === reason
                      ? "bg-red-50 border-red-200 text-red-900 font-semibold"
                      : "bg-white border-gray-150 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <span>{reason}</span>
                  {selectedPreset === reason && (
                    <span className="text-red-600 font-bold">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Additional Notes */}
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
              Additional Details (Optional)
            </label>
            <textarea
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="e.g. Patient called and requested to cancel due to fever..."
              rows="2"
              className="w-full text-xs p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-gray-800"
            />
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
          >
            Go Back
          </button>
          <button
            type="button"
            onClick={handleConfirmCancel}
            disabled={isSubmitting}
            className="px-5 py-2 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-all shadow-xs disabled:opacity-50"
          >
            {isSubmitting ? "Cancelling..." : "Confirm Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelAppointmentModal;
